import React, { useEffect, type FormEvent, useState, useRef } from "react";
import { TagInput } from "../../../shared/ui/components/TagInput";
import { UrlMetadataSuggestions } from "./UrlMetadataSuggestions";
import { useSuggestionManager } from "../hooks/useSuggestionManager";
import { useReferenceForm } from "../hooks/useReferenceForm";
import { useFormSubmission } from "../hooks/useFormSubmission";
import { MediaUploadSection } from "./MediaUploadSection";
import { AppSelectionSection } from "./AppSelectionSection";
import { SuggestedTagsSection } from "./SuggestedTagsSection";
import { 
  SuggestionField, 
  SuggestionsBanner, 
  AIAnalysisLoading, 
  AIAnalysisError 
} from "../../saved-prompts/components/AISuggestions";
import type { Reference } from "../../../shared/types/reference";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";

interface SimplifiedAddReferenceFormProps {
  existingReference?: Reference | null;
  onFormClose: () => void;
  initialImages?: File[];
}

// Helper function to safely extract hostname from a URL
const getHostnameFromUrl = (url: string): string | null => {
  try {
    // Try to construct a URL object
    const urlObj = new URL(url);
    return urlObj.hostname;
  } catch {
    // If URL is invalid, try to extract domain manually
    try {
      // Remove protocol if present
      const cleanUrl = url.replace(/^https?:\/\//, '');
      // Extract domain part (before first slash or query)
      const domain = cleanUrl.split(/[/?#]/)[0];
      // Basic validation - should contain at least one dot
      if (domain && domain.includes('.')) {
        return domain;
      }
    } catch {
      // If all fails, return null
    }
    return null;
  }
};

export function SimplifiedAddReferenceForm({ existingReference, onFormClose, initialImages }: SimplifiedAddReferenceFormProps) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [isQuickMode, setIsQuickMode] = useState(true);

  // Custom hooks for state management
  const suggestionManager = useSuggestionManager();
  const formState = useReferenceForm({ existingReference, initialImages });
  const { handleAutoFillWithAI, handleSubmit } = useFormSubmission({
    existingReference,
    suggestionManager,
    onFormClose,
    createReference: formState.createReference,
    updateReference: formState.updateReference,
    generateUploadUrl: formState.generateUploadUrl,
    addTagToRef: formState.addTagToRef,
    createApp: formState.handleCreateApp
  });

  // Debug logging
  console.log('SimplifiedAddReferenceForm render - formState.imageFiles:', formState.imageFiles, 'length:', formState.imageFiles.length);

  // Auto-apply suggestions when they're accepted
  useEffect(() => {
    if (suggestionManager.acceptedCount > 0) {
      const acceptedValues = suggestionManager.getAcceptedValues();
      
      Object.entries(acceptedValues).forEach(([field, value]) => {
        switch (field) {
          case 'title':
            if (!formState.title) formState.setTitle(value);
            break;
          case 'description':
            if (!formState.description) formState.setDescription(value);
            break;
          case 'sourceURL':
            if (!formState.sourceURL) formState.setSourceURL(value);
            break;
          case 'category':
            if (!formState.category) formState.setCategory(value);
            break;
          case 'type':
            if (value && ['screenshot', 'component', 'flow', 'screencast'].includes(value)) {
              formState.setType(value as typeof formState.type);
            }
            break;
          case 'tags': {
            const newTags = value.split(',').map(tag => tag.trim()).filter(Boolean);
            newTags.forEach(tagName => {
              if (!formState.currentTags.find(t => t.name === tagName)) {
                formState.setCurrentTags(prev => [...prev, { 
                  _id: undefined as unknown as import("../../../../convex/_generated/dataModel").Id<"tags">, 
                  name: tagName 
                }]);
              }
            });
            break;
          }
          case 'appName': {
            const existingApp = formState.allApps.find((app: any) => 
              app.name.toLowerCase().includes(value.toLowerCase()) ||
              value.toLowerCase().includes(app.name.toLowerCase())
            );
            if (existingApp) {
              formState.setSelectedAppId(existingApp._id);
            } else if (!formState.selectedAppId) {
              formState.setNewApp((prev: any) => ({ ...prev, name: value }));
            }
            break;
          }
        }
      });
    }
  }, [suggestionManager.acceptedCount, suggestionManager.getAcceptedValues(), formState]);

  const handleFormSubmit = (e: FormEvent) => {
    e.preventDefault();
    
    void handleSubmit({
      title: formState.title,
      type: formState.type,
      description: formState.description,
      sourceURL: formState.sourceURL,
      category: formState.category,
      imageFiles: formState.imageFiles,
      currentTags: formState.currentTags,
      selectedAppId: formState.selectedAppId,
      newApp: formState.newApp,
      creatingApp: formState.creatingApp,
      resetForm: formState.resetForm,
      extractedThumbnail: formState.extractedThumbnail
    });
  };

  // Synchronous wrappers for TagInput
  const handleAddTagSync = () => { void formState.handleAddTag(); };
  const handleRemoveTagSync = (id: import("../../../../convex/_generated/dataModel").Id<"tags">) => { 
    void formState.handleRemoveTag(id); 
  };

  return (
    <div className="max-w-2xl mx-auto">
      <form
        onSubmit={handleFormSubmit}
        className="card bg-base-100 shadow-xl"
      >
        <div className="card-body space-y-6">
        {/* Header with mode toggle */}
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-semibold text-gray-800">
            {existingReference ? "Edit" : "Add New"} Reference
          </h2>
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={() => setIsQuickMode(!isQuickMode)}
              className={`btn btn-sm ${
                isQuickMode 
                  ? 'btn-primary' 
                  : 'btn-neutral'
              }`}
            >
              Quick Mode
            </button>
            <button
              type="button"
              onClick={() => setIsQuickMode(!isQuickMode)}
              className={`btn btn-sm ${
                !isQuickMode 
                  ? 'btn-primary' 
                  : 'btn-neutral'
              }`}
            >
              Full Mode
            </button>
          </div>
        </div>

        {/* AI Suggestions Banner */}
        {suggestionManager.hasAnySuggestions && (
          <SuggestionsBanner
            suggestions={Object.values(suggestionManager.suggestions).map(s => s.suggestion)}
            overallConfidence={suggestionManager.overallConfidence || 'low'}
            analysisNotes={suggestionManager.analysisNotes}
            onAcceptAll={suggestionManager.acceptAllSuggestions}
            onRejectAll={suggestionManager.rejectAllSuggestions}
            acceptedCount={suggestionManager.acceptedCount}
            totalCount={suggestionManager.totalCount}
          />
        )}

        {/* AI Analysis States */}
        {suggestionManager.analysisState === 'analyzing' && (
          <AIAnalysisLoading status="Analyzing your media with AI to suggest form values..." />
        )}

        {suggestionManager.analysisState === 'error' && suggestionManager.analysisError && (
          <AIAnalysisError
            error={suggestionManager.analysisError}
            onRetry={() => void handleAutoFillWithAI(formState.imageFiles)}
            onDismiss={() => suggestionManager.setAnalysisState('idle')}
          />
        )}

        {/* Essential Fields (always visible) */}
        <div className="space-y-4">
          {/* Media Upload Section - Priority 1 */}
          {(() => {
            const shouldRender = (formState.type === "screenshot" || formState.type === "component" || formState.type === "flow") || formState.imageFiles.length > 0;
            console.log('MediaUploadSection render check - type:', formState.type, 'imageFiles.length:', formState.imageFiles.length, 'shouldRender:', shouldRender);
            return shouldRender;
          })() && (
            <MediaUploadSection
              type={formState.type}
              imageFiles={formState.imageFiles}
              setImageFiles={formState.setImageFiles}
              existingImageUrl={existingReference?.imageUrl}
              extractedThumbnail={formState.extractedThumbnail}
              onAutoFillWithAI={() => void handleAutoFillWithAI(formState.imageFiles)}
              canUseAI={true}
              analysisState={suggestionManager.analysisState}
            />
          )}

          {/* Title Field - Priority 2 */}
          <SuggestionField
            suggestion={suggestionManager.getFieldSuggestion('title')?.suggestion}
            isAccepted={suggestionManager.getFieldSuggestion('title')?.status === 'accepted'}
            isRejected={suggestionManager.getFieldSuggestion('title')?.status === 'rejected'}
            onAccept={() => suggestionManager.acceptSuggestion('title')}
            onReject={() => suggestionManager.rejectSuggestion('title')}
          >
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="title"
                value={formState.title}
                onChange={(e) => formState.setTitle(e.target.value)}
                required
                placeholder="Give your reference a descriptive title..."
                className="input input-bordered w-full"
              />
            </div>
          </SuggestionField>

          {/* Quick Mode: Only show description */}
          {isQuickMode && (
            <SuggestionField
              suggestion={suggestionManager.getFieldSuggestion('description')?.suggestion}
              isAccepted={suggestionManager.getFieldSuggestion('description')?.status === 'accepted'}
              isRejected={suggestionManager.getFieldSuggestion('description')?.status === 'rejected'}
              onAccept={() => suggestionManager.acceptSuggestion('description')}
              onReject={() => suggestionManager.rejectSuggestion('description')}
            >
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                  Description
                </label>
                <textarea
                  id="description"
                  value={formState.description}
                  onChange={(e) => formState.setDescription(e.target.value)}
                  rows={3}
                  placeholder="Describe what this reference shows or why it's useful..."
                  className="textarea textarea-bordered w-full"
                />
                <SuggestedTagsSection
                  suggestedTags={formState.suggestedTags}
                  onAddSuggestedTag={formState.handleAddSuggestedTag}
                />
              </div>
            </SuggestionField>
          )}
        </div>

        {/* Advanced/Full Mode Fields */}
        {!isQuickMode && (
          <div className="space-y-4">
            {/* Type Field */}
            <SuggestionField
              suggestion={suggestionManager.getFieldSuggestion('type')?.suggestion}
              isAccepted={suggestionManager.getFieldSuggestion('type')?.status === 'accepted'}
              isRejected={suggestionManager.getFieldSuggestion('type')?.status === 'rejected'}
              onAccept={() => suggestionManager.acceptSuggestion('type')}
              onReject={() => suggestionManager.rejectSuggestion('type')}
            >
              <div>
                <label htmlFor="type" className="block text-sm font-medium text-gray-700">Type</label>
                <select
                  id="type"
                  value={formState.type}
                  onChange={(e) => formState.setType(e.target.value as typeof formState.type)}
                  className="select select-bordered w-full"
                >
                  <option value="screenshot">Screenshot</option>
                  <option value="component">UI Component</option>
                  <option value="flow">User Flow</option>
                  <option value="screencast">Screencast</option>
                </select>
              </div>
            </SuggestionField>

            {/* Description Field */}
            <SuggestionField
              suggestion={suggestionManager.getFieldSuggestion('description')?.suggestion}
              isAccepted={suggestionManager.getFieldSuggestion('description')?.status === 'accepted'}
              isRejected={suggestionManager.getFieldSuggestion('description')?.status === 'rejected'}
              onAccept={() => suggestionManager.acceptSuggestion('description')}
              onReject={() => suggestionManager.rejectSuggestion('description')}
            >
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  id="description"
                  value={formState.description}
                  onChange={(e) => formState.setDescription(e.target.value)}
                  rows={3}
                  placeholder="Describe what this reference shows or why it's useful..."
                  className="textarea textarea-bordered w-full"
                />
                <SuggestedTagsSection
                  suggestedTags={formState.suggestedTags}
                  onAddSuggestedTag={formState.handleAddSuggestedTag}
                />
              </div>
            </SuggestionField>

            {/* Tags Field */}
            <SuggestionField
              suggestion={suggestionManager.getFieldSuggestion('tags')?.suggestion}
              isAccepted={suggestionManager.getFieldSuggestion('tags')?.status === 'accepted'}
              isRejected={suggestionManager.getFieldSuggestion('tags')?.status === 'rejected'}
              onAccept={() => suggestionManager.acceptSuggestion('tags')}
              onReject={() => suggestionManager.rejectSuggestion('tags')}
            >
              <div>
                <TagInput
                  tagInput={formState.tagInput}
                  setTagInput={formState.setTagInput}
                  currentTags={formState.currentTags}
                  setCurrentTags={formState.setCurrentTags}
                  allUserTags={formState.allUserTags}
                  onAddTag={handleAddTagSync}
                  onRemoveTag={handleRemoveTagSync}
                />
              </div>
            </SuggestionField>

            {/* Collapsible Optional Fields */}
            <div className="border-t pt-4">
              <button
                type="button"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="btn btn-ghost btn-sm w-full justify-between"
              >
                <span>Optional Details</span>
                {showAdvanced ? (
                  <ChevronUpIcon className="h-4 w-4" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4" />
                )}
              </button>

              {showAdvanced && (
                <div className="mt-4 space-y-4">
                  {/* Source URL Field */}
                  <SuggestionField
                    suggestion={suggestionManager.getFieldSuggestion('sourceURL')?.suggestion}
                    isAccepted={suggestionManager.getFieldSuggestion('sourceURL')?.status === 'accepted'}
                    isRejected={suggestionManager.getFieldSuggestion('sourceURL')?.status === 'rejected'}
                    onAccept={() => suggestionManager.acceptSuggestion('sourceURL')}
                    onReject={() => suggestionManager.rejectSuggestion('sourceURL')}
                  >
                    <div>
                      <label htmlFor="sourceURL" className="block text-sm font-medium text-gray-700">Source URL</label>
                      <input
                        type="url"
                        id="sourceURL"
                        value={formState.sourceURL}
                        onChange={(e) => formState.setSourceURL(e.target.value)}
                        placeholder="https://example.com"
                        className="input input-bordered w-full"
                      />
                    </div>
                  </SuggestionField>

                  {/* Category Field */}
                  <SuggestionField
                    suggestion={suggestionManager.getFieldSuggestion('category')?.suggestion}
                    isAccepted={suggestionManager.getFieldSuggestion('category')?.status === 'accepted'}
                    isRejected={suggestionManager.getFieldSuggestion('category')?.status === 'rejected'}
                    onAccept={() => suggestionManager.acceptSuggestion('category')}
                    onReject={() => suggestionManager.rejectSuggestion('category')}
                  >
                    <div>
                      <label htmlFor="category" className="block text-sm font-medium text-gray-700">Category</label>
                      <input
                        type="text"
                        id="category"
                        value={formState.category}
                        onChange={(e) => formState.setCategory(e.target.value)}
                        placeholder="e.g., Desktop, Mobile App, Web"
                        className="input input-bordered w-full"
                      />
                    </div>
                  </SuggestionField>

                  {/* App Selection Section */}
                  <SuggestionField
                    suggestion={suggestionManager.getFieldSuggestion('appName')?.suggestion}
                    isAccepted={suggestionManager.getFieldSuggestion('appName')?.status === 'accepted'}
                    isRejected={suggestionManager.getFieldSuggestion('appName')?.status === 'rejected'}
                    onAccept={() => suggestionManager.acceptSuggestion('appName')}
                    onReject={() => suggestionManager.rejectSuggestion('appName')}
                  >
                    <AppSelectionSection
                      selectedAppId={formState.selectedAppId}
                      setSelectedAppId={formState.setSelectedAppId}
                      allApps={formState.allApps}
                      creatingApp={formState.creatingApp}
                      setCreatingApp={formState.setCreatingApp}
                      newApp={formState.newApp}
                      setNewApp={formState.setNewApp}
                      onCreateApp={() => void formState.handleCreateApp()}
                    />
                  </SuggestionField>
                </div>
              )}
            </div>
          </div>
        )}

        {/* URL Source Indicator - Show when URL is present */}
        {formState.sourceURL && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <div className="flex items-center space-x-2">
              <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
              <span className="text-sm text-gray-600 font-medium">Source URL:</span>
              <a 
                href={formState.sourceURL} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:text-blue-800 underline truncate flex-1"
                title={formState.sourceURL}
              >
                {formState.sourceURL}
              </a>
            </div>
          </div>
        )}

        {/* URL Metadata Suggestions - Show when URL is present */}
        {formState.sourceURL && (
          <UrlMetadataSuggestions
            isExtractingMetadata={formState.isExtractingMetadata}
            metadataSuggestions={formState.metadataSuggestions}
            extractedThumbnail={formState.extractedThumbnail}
            metadataError={formState.metadataError}
            onApplySuggestion={formState.applyMetadataSuggestion}
            onRejectSuggestion={formState.rejectMetadataSuggestion}
            onApplyAllSuggestions={formState.applyAllMetadataSuggestions}
            sourceURL={formState.sourceURL}
          />
        )}

        {/* Quick URL Reference Creation */}
        {formState.sourceURL && 
         formState.metadataSuggestions.length > 0 && 
         !formState.isExtractingMetadata && 
         !formState.metadataError && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">
                    Quick Reference Creation
                  </h3>
                  <p className="mt-1 text-sm text-blue-600">
                    Metadata extracted successfully! Create reference quickly with suggested data.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  // Apply all suggestions and submit
                  formState.applyAllMetadataSuggestions();
                  // Small delay to allow state updates to propagate
                  setTimeout(() => {
                    const quickFormData = {
                      title: formState.title || `Reference from ${getHostnameFromUrl(formState.sourceURL) || formState.sourceURL}`,
                      type: formState.type,
                      description: formState.description,
                      sourceURL: formState.sourceURL,
                      category: formState.category,
                      imageFiles: formState.imageFiles,
                      currentTags: formState.currentTags,
                      selectedAppId: formState.selectedAppId,
                      newApp: formState.newApp,
                      creatingApp: formState.creatingApp,
                      resetForm: formState.resetForm,
                      extractedThumbnail: formState.extractedThumbnail
                    };
                    void handleSubmit(quickFormData);
                  }, 100);
                }}
                className="ml-3 btn btn-info btn-sm"
              >
                Quick Create
              </button>
            </div>
          </div>
        )}

        {/* Form Actions */}
        <div className="flex justify-between items-center pt-6 border-t">
          <div className="text-sm text-gray-500">
            {isQuickMode ? (
              <span>Quick mode: Just add title and description to get started</span>
            ) : (
              <span>Full mode: Add detailed information for better organization</span>
            )}
          </div>
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={onFormClose}
              className="btn btn-neutral"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
            >
              {existingReference ? "Save Changes" : "Create Reference"}
            </button>
          </div>
        </div>
        </div>
      </form>
    </div>
  );
} 